import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import { getAllMeetings, editMeeting, deleteMeeting } from "@/api/misc";
import { type AppState } from "..";

interface MeetingsState {
  allMeetings: any[];
}

const initialState: MeetingsState = {
  allMeetings: [],
};

// Async thunk for getting meetings
export const getMeetingsAction = createAsyncThunk(
  "meeting/getMeetings",
  async (organizationData: { id: string }, { rejectWithValue }) => {
    try {
      const response = await getAllMeetings(organizationData.id);
      return response.data.meetings;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Async thunk for deleting a meeting
export const deleteMeetingAction = createAsyncThunk(
  "meeting/deleteMeeting",
  async (data: { organizationId: string; meetingId: string }, { getState, rejectWithValue }) => {
    try {
      const response = await deleteMeeting({
        organizationId: data.organizationId,
        meetingId: data.meetingId,
      });

      if (response.status === 204) {
        const allMeetings = (getState() as AppState).meeting.allMeetings;
        const filteredMeetings = allMeetings.filter((meeting) => meeting.id !== data.meetingId);
        return filteredMeetings;
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Async thunk for editing a meeting
export const editMeetingAction = createAsyncThunk(
  "meeting/editMeeting",
  async (
    {
      organizationId,
      meetingId,
      updatedData,
    }: { organizationId: string; meetingId: string; updatedData: any },
    { getState },
  ) => {
    const response = await editMeeting({ organizationId, meetingId }, updatedData);
    if (response.status === 200) {
      return (getState() as AppState).meeting.allMeetings.map((item) =>
        item.id === meetingId ? { ...item, ...updatedData } : item,
      );
    }
  },
);

const meetingSlice = createSlice({
  name: "meeting",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getMeetingsAction.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.allMeetings = action.payload;
      })
      .addCase(deleteMeetingAction.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.allMeetings = action.payload;
      })
      .addCase(editMeetingAction.fulfilled, (state, action: PayloadAction<any[]>) => {
        state.allMeetings = action.payload;
      });
    // Handle other cases like pending, rejected if needed
  },
});

export default meetingSlice.reducer;
